import React from 'react';
import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';
import './GuidePage.css';
import Navigation from './Navigation';
import { usePageTracking } from './hooks/useAnalytics';

function BeatErrorPage() {
  usePageTracking('beat_error_guide', {
    page_title: "Beat Error Explained: What's Acceptable"
  });

  return (
    <div className="guide-page">
      <Helmet>
        <title>Beat Error Explained: What's Acceptable | ChronoLog</title>
        <meta name="description" content="Beat error is the timing gap between a watch's tick and tock, in milliseconds. What's acceptable by movement type, what causes it, and how it's fixed." />
        <meta property="og:type" content="article" />
        <meta property="og:title" content="Beat Error Explained: What's Acceptable" />
        <meta property="og:description" content="Beat error is the timing gap between a watch's tick and tock, measured in milliseconds. Acceptable ranges by movement type, causes, and fixes." />
        <meta property="og:url" content="https://www.chronolog.watch/beat-error" />
        <meta property="og:image" content="https://www.chronolog.watch/og-image.png" />
        <link rel="canonical" href="https://www.chronolog.watch/beat-error" />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@graph": [
              {
                "@type": "Article",
                "headline": "Beat Error Explained: What's Acceptable",
                "description": "What beat error is in a mechanical watch, what causes the tick-tock asymmetry, acceptable ranges by movement type, how it's measured, and why fixing it is a watchmaker's job.",
                "author": { "@type": "Organization", "name": "ChronoLog", "url": "https://www.chronolog.watch" },
                "publisher": { "@type": "Organization", "name": "ChronoLog", "url": "https://www.chronolog.watch" },
                "url": "https://www.chronolog.watch/beat-error",
                "datePublished": "2026-10-12",
                "keywords": "watch beat error, beat error acceptable range, beat error meaning, timegrapher beat error, mechanical watch, impulse pin, how to fix beat error"
              },
              {
                "@type": "FAQPage",
                "mainEntity": [
                  {
                    "@type": "Question",
                    "name": "What is an acceptable beat error?",
                    "acceptedAnswer": {
                      "@type": "Answer",
                      "text": "For a modern movement, most watchmakers consider beat error under 0.5 ms excellent and under about 1.0 ms perfectly acceptable. Vintage movements without a movable stud carrier are commonly accepted up to around 2 ms, because correcting them means rotating the hairspring collet on the balance staff. Published tolerances vary by manufacturer and watchmaker, so treat these as typical working ranges rather than official standards."
                    }
                  },
                  {
                    "@type": "Question",
                    "name": "Does beat error affect accuracy?",
                    "acceptedAnswer": {
                      "@type": "Answer",
                      "text": "At small values, barely. Daily rate is the average of the tick and tock intervals, so a fast half-swing and a slow half-swing largely cancel out. A large beat error matters for other reasons: it can make a watch harder to self-start when the mainspring is nearly unwound, and it signals that the escapement is out of adjustment, which often accompanies other problems worth a watchmaker's attention."
                    }
                  },
                  {
                    "@type": "Question",
                    "name": "Can beat error be adjusted at home?",
                    "acceptedAnswer": {
                      "@type": "Answer",
                      "text": "Not realistically. Adjusting beat error means opening the case and moving the hairspring stud carrier a fraction of a millimeter near the balance wheel, the most delicate part of the movement, while watching a timegrapher. On older movements without a movable stud, it requires removing the balance and rotating the hairspring collet. One slip can bend or break the hairspring, so this is a job for a watchmaker."
                    }
                  }
                ]
              }
            ]
          })}
        </script>
      </Helmet>

      <Navigation showAndroidButton={false} />

      <article className="article">
        <h1>Watch Beat Error Explained</h1>
        <div className="article-meta">October 2026</div>

        <p className="article-lead">
          Beat error is the difference, in milliseconds, between the two halves of a balance wheel's swing — the time from tick to tock versus tock to tick. In a perfectly adjusted watch both intervals are equal and the beat error is 0.0 ms. In practice, anything under about 0.5 ms is excellent for a modern movement, and under 1.0 ms is generally fine.
        </p>

        <p>
          You can sometimes hear it. A watch with significant beat error doesn't tick evenly — it sounds like a limp: <em>tick-tock... tick-tock...</em> instead of a steady, metronome-even beat. On a timegrapher it shows up as two parallel trace lines instead of one.
        </p>

        <h2>What causes beat error</h2>

        <p>
          Inside the movement, the balance wheel carries a small jewel called the impulse pin, which engages the pallet fork — the lever that releases the gear train one tooth at a time. When the watch is "in beat," the impulse pin sits exactly on the line between the balance pivot and the pallet fork pivot while the balance is at rest. Each swing then travels an equal distance in both directions before unlocking the escapement.
        </p>

        <p>
          If the hairspring is positioned so that the resting point is slightly off that center line, one half of the swing arrives early and the other arrives late. That asymmetry is the beat error. Common causes include factory adjustment tolerances, a hairspring disturbed during a service, a shock that shifted the collet (the hub that fixes the hairspring to the balance staff), or wear and age in vintage movements.
        </p>

        <h2>Acceptable ranges by movement type</h2>

        <p>
          There is no single industry standard for beat error the way <Link to="/accuracy">chronometer standards define daily rate</Link> — tolerances come from manufacturers' service guidelines and watchmaking practice, and they vary by source. The working ranges below are typical:
        </p>

        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>Movement type</th>
                <th>Typical target</th>
                <th>Commonly accepted</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>Modern high-grade / chronometer-grade</td>
                <td>0.0–0.3 ms</td>
                <td>Up to ~0.5 ms</td>
              </tr>
              <tr>
                <td>Modern standard (ETA, Sellita, Miyota, Seiko)</td>
                <td>0.0–0.5 ms</td>
                <td>Up to ~1.0 ms</td>
              </tr>
              <tr>
                <td>Vintage, with movable stud carrier</td>
                <td>Under 1.0 ms</td>
                <td>Up to ~1.5 ms</td>
              </tr>
              <tr>
                <td>Vintage, fixed stud (collet adjustment only)</td>
                <td>Under 1.5 ms</td>
                <td>Up to ~2.0 ms</td>
              </tr>
            </tbody>
          </table>
        </div>

        <p>
          Why the tolerance for vintage pieces? On movements without a movable stud carrier, correcting beat error means removing the balance and rotating the hairspring collet by hand — risky, time-consuming work. Many watchmakers sensibly leave a harmless 1.5 ms alone rather than risk a seventy-year-old hairspring.
        </p>

        <h2>Does beat error affect timekeeping?</h2>

        <p>
          Less than you'd expect. Your watch's daily rate is the <em>average</em> beat interval, so a half-swing that runs long and a half-swing that runs short mostly cancel out. A watch with 2 ms of beat error can still keep excellent time.
        </p>

        <p>
          Large beat error matters for two other reasons. First, self-starting: a badly out-of-beat watch needs a bigger push to get the balance swinging, so it may hesitate to restart when the mainspring is nearly unwound. Second, it's a symptom. A movement that left the factory in beat and now shows 2.5 ms has been shocked, poorly serviced, or disturbed — and whatever moved the hairspring may have caused other trouble too.
        </p>

        <h2>How beat error is measured</h2>

        <p>
          A <Link to="/what-is-a-timegrapher">timegrapher</Link> listens to the escapement and timestamps each tick and tock, then reports the difference between the alternating intervals. On a 28,800 bph movement the beats are nominally 125 ms apart; if the machine hears gaps of 124 ms and 126 ms alternating, that's a 2.0 ms beat error.
        </p>

        <p>
          You don't need dedicated hardware to get a reading. The ChronoLog app includes an audio timegrapher that measures rate and beat error through your phone's microphone, with no extra equipment — the <Link to="/timegrapher">signal processing behind it is a story of its own</Link>. Dedicated machines resolve beat error more precisely, but a phone measurement is plenty to tell a healthy 0.3 ms from a worrying 2.5 ms.
        </p>

        <p>
          Measure in a quiet room and check more than one position (dial up, crown down), since readings can shift slightly between positions. If you're checking overall health, measure <Link to="/measure-watch-accuracy">daily rate</Link> at the same time — rate and beat error together tell you far more than either alone.
        </p>

        <h2>Can you fix beat error yourself?</h2>

        <p>
          Honestly: no. On most modern movements the fix is simple <em>for a watchmaker</em> — rotate the movable stud carrier a fraction of a millimeter while watching a timegrapher until the two trace lines merge. But that lever sits next to the hairspring, the most fragile component in the watch. One slip bends it, and a bent hairspring is a far more expensive problem than the beat error was.
        </p>

        <p>
          On vintage movements with a fixed stud, the adjustment requires removing the balance assembly and rotating the collet — genuinely skilled work. If your watch shows high beat error along with poor rate or low amplitude, that combination usually points to a service rather than a single adjustment.
        </p>

        <p>
          The practical takeaway: measure it, know your number, and if it's under a millisecond on a modern watch, relax. If it's well beyond the ranges above — or it suddenly changed — let a professional open the case.
        </p>

        <div className="article-cta">
          <h2>Check your watch's beat error</h2>
          <p>
            ChronoLog's audio timegrapher measures rate and beat error through your phone's microphone — $5.99 one-time on the <a href="https://apps.apple.com/us/app/chronolog-watch-accuracy/id6452083510">App Store</a>.
          </p>
        </div>

        <div className="related-guides">
          <h2>Related guides</h2>
          <ul>
            <li><Link to="/accuracy">How accurate should a mechanical watch be?</Link></li>
            <li><Link to="/what-is-a-timegrapher">What is a timegrapher? Readings explained</Link></li>
            <li><Link to="/measure-watch-accuracy">How to measure watch accuracy at home</Link></li>
            <li><Link to="/timegrapher">Building a timegrapher from a phone microphone</Link></li>
          </ul>
        </div>
      </article>
    </div>
  );
}

export default BeatErrorPage;
